import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogIn, LogOut, QrCode, Fingerprint } from 'lucide-react';
import clsx from 'clsx';
import { type AttendanceDayDto, type AttendanceTotals, type ClockStatusDto, type PeriodDto, type PeriodEmployeeSummary } from '@workforce/shared';
import { formatMinutes, periodName, t } from '@/lib/i18n';
import { fmtDate } from '@/lib/format';
import { api, qs } from '@/lib/api';
import { useCurrentPeriod } from '@/lib/periods';
import { useAuth } from '@/lib/auth';
import { useTz } from '@/lib/timezone';
import { Badge, Button, Card, ErrorBanner, Field, Input, Notice, PageHeader, Select, Spinner } from '@/components/ui';
import { ExportMenu } from '@/components/ExportMenu';
import { AttendanceBadge, AttendanceTable, Diff, PunchList, RANGE_OPTIONS, TotalsBar, rangeFor, type RangePreset } from '@/components/AttendanceTable';

export function useClockStatus(enabled = true) {
  return useQuery({
    queryKey: ['me', 'attendance', 'status'],
    queryFn: () => api.get<ClockStatusDto>('/me/attendance/status'),
    refetchInterval: 60_000,
    enabled,
  });
}

/** Worked minutes that keep ticking while clocked in. */
export function useLiveMinutes(status: ClockStatusDto | undefined, fetchedAt: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!status?.clockedIn) return;
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, [status?.clockedIn]);
  if (!status) return 0;
  const extra = status.clockedIn ? Math.max(0, Math.floor((now - fetchedAt) / 60_000)) : 0;
  return status.today.workedMinutes + extra;
}

export function ClockCard({ compact }: { compact?: boolean }) {
  const qc = useQueryClient();
  const { fmt } = useTz();
  const q = useClockStatus();
  const live = useLiveMinutes(q.data, q.dataUpdatedAt);
  const clock = useMutation({
    mutationFn: (direction: 'in' | 'out') => api.post<ClockStatusDto>('/me/attendance/clock', { direction }),
    onSuccess: (d) => {
      qc.setQueryData(['me', 'attendance', 'status'], d);
      qc.invalidateQueries({ queryKey: ['me', 'attendance', 'days'] });
    },
  });
  if (q.isLoading) return <Card><Spinner /></Card>;
  if (!q.data) return <ErrorBanner error={q.error} />;
  const s = q.data;
  const today = s.today;
  const tz = today.timezone;
  return (
    <Card title={t('Today')} actions={<AttendanceBadge day={today} />}>
      <ErrorBanner error={clock.error} />
      <div className={clsx('grid gap-5', compact ? 'grid-cols-1' : 'md:grid-cols-[1fr_auto]')}>
        <div>
          <p className="text-4xl font-semibold tabular-nums tracking-tight text-slate-900">{formatMinutes(live)}</p>
          <p className="mt-1 text-sm text-slate-500">
            {today.scheduledMinutes ? t('of {time} expected', { time: formatMinutes(today.scheduledMinutes) }) : today.holidayName ?? (today.status === 'leave' ? t('On leave today') : t('No work scheduled today'))}
            {today.scheduledStart && today.scheduledMinutes > 0 && ` · ${fmt(today.scheduledStart, tz, 'HH:mm')}–${fmt(today.scheduledEnd, tz, 'HH:mm')}`}
          </p>
          {s.clockedIn && s.lastPunch && (
            <p className="mt-2 text-sm text-emerald-700">
              {t('Clocked in since {time}', { time: fmt(s.lastPunch.occurredAt, tz, 'HH:mm') })}
            </p>
          )}
          {!s.clockedIn && s.lastPunch && <p className="mt-2 text-sm text-slate-600">{t('Clocked out at {time}', { time: fmt(s.lastPunch.occurredAt, tz, 'HH:mm') })}</p>}
          {today.lateMinutes > 0 && <p className="mt-1 text-xs text-amber-700">{t('Arrived {time} late', { time: formatMinutes(today.lateMinutes) })}</p>}
        </div>
        <div className="flex flex-col justify-center gap-2 md:min-w-48">
          {s.canUseAppButton ? (
            s.clockedIn ? (
              <Button variant="secondary" className="py-3" icon={<LogOut className="h-4 w-4" />} loading={clock.isPending} onClick={() => clock.mutate('out')}>
                {t('Clock out')}
              </Button>
            ) : (
              <Button className="py-3" icon={<LogIn className="h-4 w-4" />} loading={clock.isPending} onClick={() => clock.mutate('in')}>
                {t('Clock in')}
              </Button>
            )
          ) : (
            <div className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <span className="flex gap-1 text-slate-400">
                <Fingerprint className="h-4 w-4" />
                <QrCode className="h-4 w-4" />
              </span>
              {s.appButtonReason}
            </div>
          )}
        </div>
      </div>
      {!compact && today.punches && today.punches.length > 0 && (
        <div className="mt-5 border-t border-slate-100 pt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">{t("Today's punches")}</p>
          <PunchList punches={today.punches} zone={tz} />
        </div>
      )}
    </Card>
  );
}

export function MyAttendancePage() {
  const { user } = useAuth();
  const zone = user?.employee?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const periods = useCurrentPeriod();
  const [preset, setPreset] = useState<RangePreset>('period');
  const [custom, setCustom] = useState(() => rangeFor('month', zone));
  const range = rangeFor(preset, zone, custom, periods.data);
  const q = useQuery({
    queryKey: ['me', 'attendance', 'days', range.from, range.to],
    queryFn: () => api.get<{ days: AttendanceDayDto[]; totals: AttendanceTotals }>(`/me/attendance/days${qs(range)}`),
    enabled: !preset.includes('period') || !periods.isLoading,
  });
  if (!user?.employee) {
    return (
      <div>
        <PageHeader title={t('My attendance')} />
        <Notice>{t('Your login is not linked to an employee record, so there is no personal attendance to show.')}</Notice>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <PageHeader title={t('My attendance')} subtitle={t('Times shown in {zone} unless you change the time display at the top.', { zone })} />
      <ClockCard />
      <Card
        title={t('History')}
        actions={
          <div className="flex flex-wrap items-end gap-2">
            <ExportMenu kind="my-attendance" params={{ from: range.from, to: range.to }} />
            <Select value={preset} onChange={(e) => setPreset(e.target.value as RangePreset)} aria-label={t('Period')} className="w-36">
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.label)}
                </option>
              ))}
            </Select>
          </div>
        }
        bodyClass="p-0"
      >
        {preset === 'custom' && (
          <div className="flex flex-wrap gap-3 border-b border-slate-100 px-5 py-3">
            <Field label={t('From')}>
              <Input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
            </Field>
            <Field label={t('To')}>
              <Input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
            </Field>
          </div>
        )}
        <div className="p-4">
          <ErrorBanner error={q.error} />
          {q.isLoading ? <Spinner /> : q.data && <TotalsBar totals={q.data.totals} />}
        </div>
        {q.data && <AttendanceTable days={q.data.days} />}
        <p className="px-5 py-3 text-xs text-slate-500">
          {t('Something wrong? Ask your administrator to correct it — every correction is recorded with a reason.')}
        </p>
      </Card>
      <MyPeriods
        onPick={(p) => {
          setCustom({ from: p.startDate, to: p.endDate });
          setPreset('custom');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
}

function MyPeriods({ onPick }: { onPick: (p: PeriodDto) => void }) {
  const q = useQuery({
    queryKey: ['me', 'periods'],
    queryFn: () => api.get<{ period: PeriodDto; summary: PeriodEmployeeSummary | null; source: 'live' | 'closed' }[]>('/me/periods'),
  });
  if (!q.data?.length) return null;
  return (
    <Card title={t('Reporting periods')} bodyClass="p-0">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2">{t('Period')}</th>
              <th className="px-3 py-2 text-end">{t('Worked')}</th>
              <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Expected')}</th>
              <th className="px-3 py-2 text-end">+/−</th>
              <th className="hidden px-3 py-2 text-end md:table-cell">{t('Late')}</th>
              <th className="hidden px-3 py-2 text-end md:table-cell">{t('Absent')}</th>
              <th className="hidden px-3 py-2 text-end md:table-cell">{t('Leave')}</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map(({ period: p, summary: s }) => (
              <tr key={p.id} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={() => onPick(p)}>
                <td className="px-3 py-2">
                  <span className="font-medium">{periodName(p.label)}</span>{' '}
                  {p.status === 'closed' ? <Badge>{t('Closed')}</Badge> : p.isCurrent ? <Badge tone="brand">{t('Current')}</Badge> : <Badge tone="amber">{t('Being finalised')}</Badge>}
                  <p className="text-xs text-slate-500">{t('{start} → {end}', { start: fmtDate(p.startDate, 'd LLL'), end: fmtDate(p.endDate, 'd LLL yyyy') })}</p>
                </td>
                <td className="px-3 py-2 text-end font-medium tabular-nums">{s ? formatMinutes(s.attendance.workedMinutes) : '—'}</td>
                <td className="hidden px-3 py-2 text-end tabular-nums text-slate-500 sm:table-cell">{s ? formatMinutes(s.attendance.scheduledMinutes) : '—'}</td>
                <td className="px-3 py-2 text-end tabular-nums">{s ? <Diff minutes={s.attendance.differenceMinutes} /> : '—'}</td>
                <td className="hidden px-3 py-2 text-end tabular-nums md:table-cell">{s?.attendance.lateDays || '—'}</td>
                <td className="hidden px-3 py-2 text-end tabular-nums md:table-cell">{s?.attendance.absentDays || '—'}</td>
                <td className="hidden px-3 py-2 text-end tabular-nums md:table-cell">{s?.attendance.leaveDays || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-5 py-3 text-xs text-slate-500">{t('Closed periods show the figures as they were finalised. Click a period to see its days.')}</p>
    </Card>
  );
}
